/* eslint-disable complexity */
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useSingleFetchCart } from '@/data/Content/Cart';
import { RegistrationOrigin } from '@/data/Content/Registration';
import {
	PERSONALIZATION_LANDING_PATH,
	PERSONALIZATION_LOGIN_ORIGIN,
} from '@/data/Content/RepurchaseCoupon';
import { useLocalization } from '@/data/Localization';
import { useCurrentRoute } from 'integration/hooks/useCurrentRoute';
import { useGetOriginPathQuery } from 'integration/hooks/useGetOriginPathQuery';
import { usePreviousRouteContext } from 'integration/hooks/usePreviousRoute';
import { getLocalStorageCart } from 'integration/utils/localStorageCart';
import valueIsString from 'integration/utils/typeAssertions/valueIsString';
import { useCallback, useMemo } from 'react';
import type { ParsedUrlQuery } from 'querystring';
import type { UrlObject } from 'url';

interface UseReplaceAuthRouteArgs {
	shouldCallToModal: boolean;
	origin?: RegistrationOrigin;
}

const EXCLUDE_PATHS = ['error-de-sesion'];

const RELATIVE_ORIGIN = 'http://relative.local';

export const toRouteObject = (path: string): UrlObject => {
	const url = new URL(path, RELATIVE_ORIGIN);
	const query: ParsedUrlQuery = {};
	url.searchParams.forEach((value, key) => {
		const current = query[key];
		query[key] = current === undefined ? value : [current, value].flat();
	});
	return { pathname: url.pathname, query, hash: url.hash || undefined };
};

export const useReplaceAuthRoute = ({ shouldCallToModal, origin }: UseReplaceAuthRouteArgs) => {
	const router = useNextRouter();
	const RouteLocal = useLocalization('Routes');
	const currentRoute = useCurrentRoute();
	const prevRoute = usePreviousRouteContext();
	const originPath = useGetOriginPathQuery();

	const prevClean = useMemo(() => {
		try {
			if (valueIsString(prevRoute)) {
				const prevRouteExcluded = EXCLUDE_PATHS.find((path) => prevRoute.includes(path));
				return prevRouteExcluded ? null : prevRoute;
			} else if (prevRoute?.pathname) {
				const prevRouteExcluded = EXCLUDE_PATHS.find((path) => prevRoute.pathname.includes(path));
				return prevRouteExcluded ? null : prevRoute.pathname;
			}
			return null;
		} catch {
			return null;
		}
	}, [prevRoute]);

	const { getCart } = useSingleFetchCart();

	const onReplaceAuthRoute = useCallback(async () => {
		const isQuickCart = origin === 'quick_cart' || originPath === 'quick_cart';
		const isCheckout = router.query.flow === 'checkout';
		if (isCheckout || isQuickCart) {
			const accountCart = await getCart();
			const localStorageCart = getLocalStorageCart();
			const accountCartCount = accountCart?.orderItem.length || 0;
			const localStorageCartCount = localStorageCart?.partNumbers.length;
			if (
				accountCartCount > 0 &&
				localStorageCartCount !== accountCartCount &&
				isQuickCart &&
				!isCheckout
			) {
				await router.push(RouteLocal.Cart.route.t());
				return;
			}
			await router.push(RouteLocal.CheckOut.route.t());
		} else if (originPath === PERSONALIZATION_LOGIN_ORIGIN) {
			await router.push(`/${PERSONALIZATION_LANDING_PATH}`);
		} else if (shouldCallToModal) {
			await router.push(toRouteObject(currentRoute));
		} else if (prevClean) {
			await router.push(toRouteObject(prevClean));
		} else {
			await router.push(RouteLocal.Account.route.t());
		}
	}, [router, currentRoute, shouldCallToModal, RouteLocal, prevClean, origin, originPath, getCart]);

	return {
		onReplaceAuthRoute,
	};
};
